/**
 * Editing a contact person that already exists.
 *
 * Until this form a contact could only be added, made primary or deleted, so
 * fixing a typo in a phone number meant deleting the person and typing them in
 * again - which also drops their portal link, their call history and the
 * activities that point at them. The 39 clients that came over from the old CRM
 * carry their whole address on the CONTACT rather than on the company, so this
 * is where most of the wrong data actually sits.
 *
 * Only the fields that changed are sent (`changedFields`, shared with
 * `ClientEditModal`), so saving does not rewrite a column this form only read,
 * and an emptied box is sent as null rather than dropped - clearing a wrong
 * address has to be possible. The route accepts null since `lib/contactBody.ts`.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { updateContact, type Contact } from "../../../lib/clients";
import { changedFields } from "../../../lib/clientPrefill";
import { Btn, Modal, useToast } from "../../ui";
import { Field, Pill } from "../_shared/formBits";

/** Every text field this form owns. Kept in one place so the patch and the
 *  initial state cannot drift apart. */
const TEXT_FIELDS = ["firstName", "lastName", "position", "email", "phone"] as const;

type TextField = (typeof TEXT_FIELDS)[number];
type TextState = Record<TextField, string>;

function initialText(c: Contact): TextState {
  const out = {} as TextState;
  for (const key of TEXT_FIELDS) {
    const v = c[key];
    out[key] = v == null ? "" : String(v);
  }
  return out;
}

export function ContactEditModal({
  clientId,
  contact,
  onClose,
  onSaved,
}: {
  clientId: string;
  contact: Contact;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const toast = useToast();

  const [text, setText] = useState<TextState>(() => initialText(contact));
  const [busy, setBusy] = useState(false);

  function set(key: TextField, value: string): void {
    setText((prev) => ({ ...prev, [key]: value }));
  }

  /**
   * What a changed e-mail address does NOT do, said before it is saved.
   *
   * The portal link is `Contact.userId` and the pending invite is matched by
   * contact id, so neither of them follows this box. Somebody correcting a
   * misspelt address would otherwise reasonably believe they had just moved the
   * person's sign-in, or re-pointed an invite that is still sitting in the old
   * inbox. Only shown once the address is actually different.
   */
  const emailChanged = text.email.trim() !== (contact.email ?? "").trim();
  const emailNote = !emailChanged
    ? null
    : contact.portalUser
      ? t("clients.contactEmailPortalNote", { email: contact.portalUser.email })
      : contact.portalInvitePending
        ? t("clients.contactEmailInviteNote", { email: contact.email ?? "" })
        : null;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!text.firstName.trim() || busy) return;
    setBusy(true);
    try {
      const patch = changedFields(
        contact as unknown as Record<string, unknown>,
        text,
        TEXT_FIELDS,
      );

      // Nothing to send is a normal outcome, not an error: the operator opened
      // the form, looked, and closed it.
      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }

      await updateContact(clientId, contact.id, patch);
      onSaved();
      onClose();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const title = t("clients.contactEditTitle");

  return (
    <Modal title={title} label={title} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Field label={t("clients.firstName")}>
              <Pill value={text.firstName} onChange={(v) => set("firstName", v)} placeholder={t("clients.firstName")} />
            </Field>
          </div>
          <div>
            <Field label={t("clients.lastName")}>
              <Pill value={text.lastName} onChange={(v) => set("lastName", v)} placeholder={t("clients.lastName")} />
            </Field>
          </div>
        </div>

        <Field label={t("clients.position")}>
          <Pill value={text.position} onChange={(v) => set("position", v)} placeholder={t("clients.position")} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Field label={t("clients.email")}>
              <Pill value={text.email} onChange={(v) => set("email", v)} placeholder={t("common.example.personEmail")} type="email" inputMode="email" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.phone")}>
              <Pill value={text.phone} onChange={(v) => set("phone", v)} placeholder="+372 5xxx xxxx" inputMode="tel" />
            </Field>
          </div>
        </div>

        {emailNote ? <p className="mt-1.5 text-[11px] text-muted-foreground">{emailNote}</p> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("clients.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !text.firstName.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("clients.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
